import Button from "components/button";
import Pagination from "components/pagination";
import { Breadcrumbs } from "components/breadcrumbs";
import { RoleTable, RoleCard, Filter } from "./components";
import { Link } from "react-router-dom";
import { PlusIcon } from '@heroicons/react/solid'
import { FullScreenLoading } from "components/fullScreenLoading";
import { useMemo } from "react";
import toast from "react-hot-toast";
import { useTable, useUpdate } from "@refinedev/core";
import { IOrgRole } from "interfaces";
import { isHaveActionPermission } from "utills/helpers";

export const OrgRoleList: React.FC = () => {
  const {
    tableQueryResult,
    filters,
    setFilters,
  } = useTable<IOrgRole>({
    resource: "organization/role",
    syncWithLocation: true,
    pagination: {
      mode: "off"
    },
    meta: {
      resource: "roles",
    },
  });


  const { mutate: updateMutation, isLoading: updateMutationIsLoading } = useUpdate();

  const _toggleActive = (id: string, isActive: boolean) => {
    if (!isHaveActionPermission("role_management", "organization", "edit"))
      return;
    if (window.confirm(`Are you sure you want to ${isActive ? "deactivate" : "activate"}  this Role?`)) {
      updateMutation({
        resource: "organization/role",
        id,
        meta: {
          URLSuffix: `active/${!isActive}`,
          ignoreResourceWrapper: true
        },
        values: {},
      },
      {
        onSuccess: () => {
          toast.success("Update successfully");
        },
      });
    }
  };

  const currentFilterValues = useMemo(() => {
    const logicalFilters = filters.flatMap((item) =>
      "field" in item ? item : [],
    );

    return {
      q: logicalFilters.find((item) => item.field === "q")?.value || "",
      is_active: logicalFilters.find((item) => item.field === "is_active")?.value || "",
      organization_id: logicalFilters.find((item) => item.field === "organization_id")?.value || "",
    };
  }, [filters]);

  const _handlePageChanged = (page: number) => {
    const updatedFilters = [...filters].flatMap(item => "field" in item ? item: []);
    const pageIndex = updatedFilters.findIndex(filter => filter.field === "page");
    if (pageIndex < 0) {
      updatedFilters.push({field: "page", operator: "eq", value: page})
    } else {
      updatedFilters[pageIndex].value = page;
    }

    setFilters(updatedFilters);
  }

  if (tableQueryResult.isLoading) {
    return <FullScreenLoading />
  }

  return (
    <div className="wrapper">
      <Breadcrumbs
        {...(isHaveActionPermission(
          "role_management",
          "organization",
          "create"
        ) && {
          additionalLabel: (
            <Link to={"/organization/roles/create"} target="_blank">
              <Button>
                <span className="sr-only">Create New Role</span>
                <PlusIcon className="w-4 h-4 text-white" />{" "}
                <span className="hidden sm:block sm:ml-2">Create New Role</span>
              </Button>
            </Link>
          ),
        })}
      />
      <div className="box-container">
        <Filter currentFilterValues={currentFilterValues} setFilters={setFilters} />
      </div>
      <div className="box-container">
        {!tableQueryResult.data?.data?.length &&
        !tableQueryResult.isFetching && (
          <div className="text-center">
            No data
          </div>
        )}
        {tableQueryResult?.data?.data && tableQueryResult?.data?.data.length > 0 ? (
          <>
            <div className="sm:hidden">
              <RoleCard 
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
            <div className="hidden sm:block">
              <RoleTable
                data={tableQueryResult?.data?.data}
                pageSize={tableQueryResult?.data?.pageSize}
                currentPage={tableQueryResult?.data?.paging.current_page}
                _toggleActive={_toggleActive}
              />
            </div>
          </>
        ) : <></>}
        {tableQueryResult?.data?.paging ? (
          <div className="mt-6">
            <Pagination 
              currentPage={tableQueryResult?.data?.paging.current_page}
              totalPage={tableQueryResult?.data?.paging.total_page}
              total={{
                totalItems: tableQueryResult?.data?.paging.total_item || 0,
                total: tableQueryResult?.data?.paging.total
              }}
              onChange={_handlePageChanged}
            />
          </div>
        ) : <></>}
      </div>
      {(tableQueryResult.isFetching && tableQueryResult.isPreviousData) || updateMutationIsLoading ? <FullScreenLoading /> : <></>}
    </div>
  )
};